import React from "react";
import { cn } from "@/lib/utils";

/**
 * Monochrome partner logos.
 * SVG files in /public/logos are the vendors' own vector marks (Guesty wordmark, Hostaway's
 * header wordmark, Lodgify's 2026 wordmark, Smoobu's site wordmark). They are rendered through a CSS mask so every
 * logo inherits `currentColor` — the shapes stay genuine, the wall stays calm (ink on cream),
 * and hover/brand colour is handled purely with text colour utilities.
 */
const MASKED = {
  guesty: { src: "/logos/guesty.svg", ratio: 443.5 / 116.39, h: 26 },
  hostaway: { src: "/logos/hostaway.svg", ratio: 135 / 32, h: 24 },
  lodgify: { src: "/logos/lodgify.svg", ratio: 1386 / 352, h: 24 },
  smoobu: { src: "/logos/smoobu.svg", ratio: 119 / 20, h: 19 },
  vrbo: { src: "/logos/vrbo.svg", ratio: 125 / 40, h: 30 },
  bookingMark: { src: "/logos/booking-mark.svg", ratio: 1, h: 22 },
  kartverket: { src: "/logos/kartverket.svg", ratio: 140.01 / 46.12, h: 36 },
  openai: { src: "/logos/openai.svg", ratio: 1180 / 320, h: 22 },
  digihome: { src: "/logos/digihome.svg", ratio: 928.5 / 223.5, h: 26 },
  airbnbMark: { src: "/logos/airbnb-mark.svg", ratio: 1, h: 24 },
  googleMapsMark: { src: "/logos/google-maps-mark.svg", ratio: 1, h: 24 },
};

export const BRAND_NAMES = {
  digihome: "DigiHome",
  guesty: "Guesty",
  lodgify: "Lodgify",
  hostaway: "Hostaway",
  smoobu: "Smoobu",
  hostfully: "Hostfully",
  beds24: "Beds24",
  uplisting: "Uplisting",
  hospitable: "Hospitable",
  airbnb: "Airbnb",
  booking: "Booking.com",
  vrbo: "Vrbo",
  kartverket: "Kartverket",
  googlemaps: "Google Maps",
  openai: "OpenAI",
  osm: "OpenStreetMap",
  photon: "Photon",
  airdna: "AirDNA",
  pricelabs: "PriceLabs",
};

function Masked({ id, scale = 1, className }) {
  const m = MASKED[id];
  const h = Math.round(m.h * scale);
  const url = `url("${m.src}")`;
  return (
    <span
      aria-hidden
      className={cn("inline-block shrink-0 bg-current", className)}
      style={{
        height: h,
        width: Math.round(h * m.ratio),
        WebkitMaskImage: url,
        maskImage: url,
        WebkitMaskRepeat: "no-repeat",
        maskRepeat: "no-repeat",
        WebkitMaskSize: "contain",
        maskSize: "contain",
        WebkitMaskPosition: "center",
        maskPosition: "center",
      }}
    />
  );
}

/** Typographic wordmark for partners without a redistributable vector logo. */
function Wordmark({ children, scale = 1, className, weight = "font-bold", tracking = "tracking-[-0.045em]" }) {
  return (
    <span aria-hidden className={cn("font-heading leading-none whitespace-nowrap select-none", weight, tracking, className)} style={{ fontSize: Math.round(24 * scale) }}>
      {children}
    </span>
  );
}

export function BrandLogo({ id, scale = 1, className }) {
  const label = BRAND_NAMES[id] || id;
  let node;
  switch (id) {
    case "airbnb":
      node = (
        <span className="inline-flex items-center gap-1.5">
          <Masked id="airbnbMark" scale={scale} />
          <Wordmark scale={scale} tracking="tracking-[-0.03em]" weight="font-bold">airbnb</Wordmark>
        </span>
      );
      break;
    case "booking":
      node = (
        <span className="inline-flex items-center gap-1.5">
          <Masked id="bookingMark" scale={scale} className="rounded-[4px]" />
          <Wordmark scale={scale} tracking="tracking-[-0.03em]" weight="font-bold">Booking.com</Wordmark>
        </span>
      );
      break;
    case "googlemaps":
      node = (
        <span className="inline-flex items-center gap-1.5">
          <Masked id="googleMapsMark" scale={scale} />
          <Wordmark scale={scale * 0.92} tracking="tracking-[-0.02em]" weight="font-medium">Google Maps</Wordmark>
        </span>
      );
      break;
    case "osm":
      node = <Wordmark scale={scale * 0.85} tracking="tracking-[-0.02em]" weight="font-bold">OpenStreetMap</Wordmark>;
      break;
    case "airdna":
      node = <Wordmark scale={scale} tracking="tracking-[-0.03em]" weight="font-bold">AirDNA</Wordmark>;
      break;
    case "pricelabs":
      node = <Wordmark scale={scale} tracking="tracking-[-0.03em]" weight="font-bold">PriceLabs</Wordmark>;
      break;
    case "photon":
      node = <Wordmark scale={scale} tracking="tracking-[-0.02em]" weight="font-bold">Photon</Wordmark>;
      break;
    case "hostfully":
      node = <Wordmark scale={scale * 0.9} tracking="tracking-[-0.035em]" weight="font-bold">hostfully</Wordmark>;
      break;
    case "beds24":
      node = <Wordmark scale={scale * 0.9} tracking="tracking-[-0.02em]" weight="font-extrabold">Beds24</Wordmark>;
      break;
    case "uplisting":
      node = <Wordmark scale={scale * 0.9} tracking="tracking-[-0.03em]" weight="font-semibold">Uplisting</Wordmark>;
      break;
    case "hospitable":
      node = <Wordmark scale={scale * 0.9} tracking="tracking-[-0.03em]" weight="font-bold">hospitable</Wordmark>;
      break;
    default:
      node = MASKED[id] ? <Masked id={id} scale={scale} /> : <Wordmark scale={scale}>{label}</Wordmark>;
  }
  return (
    <span role="img" aria-label={label} className={cn("inline-flex items-center text-current", className)} data-testid={`brand-logo-${id}`}>
      {node}
    </span>
  );
}
